import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import * as RadixSelect from "@radix-ui/react-select";
import React from "react";
import { up } from "~/features/app/screens";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { Icon } from "~/features/ui/icon";
import CaretDown from "~/public/icons/caret-down.svg";
import CaretUp from "~/public/icons/caret-up.svg";
type Option = {
  label: string;
  value: string;
};
type Props = {
  defaultValue?: string;
  label: string;
  name: string;
  onChange?: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  value?: string;
};
const Trigger = /*#__PURE__*/_styled(RadixSelect.Trigger).withConfig({
  componentId: "sc-sf-1l1zd4f-0"
})<{
  $hasValue: boolean;
}>`
  &[type="button"] {
    ${{
  "backgroundColor": "var(--bg-primary)",
  "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out"
}}
    ${{
  "display": "flex",
  "height": "4rem",
  "alignItems": "center",
  "justifyContent": "space-between",
  "columnGap": "0.8rem",
  "whiteSpace": "nowrap",
  "borderRadius": "2px",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "outlineWidth": "0px",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.016rem",
    "fontWeight": "400"
  }
}}
    ${{
  "&:hover": {
    "--tw-border-opacity": "1",
    "borderColor": "rgb(92 118 255 / var(--tw-border-opacity))",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(92 118 255 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  }
}}
    ${{
  "&:focus-visible": {
    "--tw-ring-offset-shadow": "var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
    "--tw-ring-shadow": "var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
    "boxShadow": "var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)",
    "--tw-ring-opacity": "1",
    "--tw-ring-color": "rgb(75 46 248 / var(--tw-ring-opacity))",
    "--tw-ring-offset-width": "2px"
  }
}}

    ${({
  $hasValue
}) => $hasValue && {
  "--tw-border-opacity": "1",
  "borderColor": "rgb(75 46 248 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
  "--tw-text-opacity": "1",
  "color": "rgb(255 255 255 / var(--tw-text-opacity))"
}}
  }
`;
const SelectIcon = _styled(RadixSelect.Icon).withConfig({
  componentId: "sc-sf-1l1zd4f-1"
})({
  "display": "inline-flex",
  "alignItems": "center"
});
const Content = /*#__PURE__*/_styled(RadixSelect.Content).withConfig({
  componentId: "sc-sf-1l1zd4f-2"
})`
  ${{
  "zIndex": "20",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(239 239 246 / var(--tw-bg-opacity))"
}}
  min-width: var(--radix-select-trigger-width);
  max-width: max-content;
  max-height: var(--radix-select-content-available-height);
`;
const Item = /*#__PURE__*/_styled(RadixSelect.Item).withConfig({
  componentId: "sc-sf-1l1zd4f-3"
})`
  ${{
  "display": "flex",
  "height": "4rem",
  "cursor": "pointer",
  "alignItems": "center",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out",
  "@media (min-width: 1024px)": {
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.016rem",
    "fontWeight": "400"
  }
}}
  ${{
  "borderColor": "rgb(0 7 4 / 0.2)",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "outlineWidth": "0px",
  "&:not(:last-child)": {
    "borderBottomWidth": "1px"
  }
}}
  ${{
  "&:hover": {
    "backgroundColor": "rgb(75 46 248 / 0.2)"
  }
}}

  &[data-highlighted] {
    ${{
  "backgroundColor": "rgb(75 46 248 / 0.2)"
}}
  }
`;
const DropdownItem = React.forwardRef<HTMLDivElement, Option>(({
  label,
  ...props
}, ref) => {
  return <Item ref={ref} {...props}>
      <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
    </Item>;
});

// Radix Select 2.x rejects empty-string Item values (reserved for clearing
// selection). Callers express "no filter / all" as `value: ""`; we map that to
// a sentinel internally and translate back at the boundary.
const ALL_SENTINEL = "__all__";
const toInternal = (v?: string) => v === "" ? ALL_SENTINEL : v;
const fromInternal = (v: string) => v === ALL_SENTINEL ? "" : v;
export function FilterDropdown(props: Props) {
  const {
    label,
    onChange,
    options,
    placeholder = "Select",
    value,
    defaultValue,
    ...rest
  } = props;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const isLg = useMediaQuery(up("lg"));
  const [open, setOpen] = React.useState(false);
  const pointerTypeRef = React.useRef("");
  const internalOptions = React.useMemo(() => options.map(option => ({
    ...option,
    value: toInternal(option.value) ?? ALL_SENTINEL
  })), [options]);
  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setOpen(false);
      return;
    }
    if (pointerTypeRef.current === "touch") return;
    setOpen(true);
  }
  function handlePointerDown(e: React.PointerEvent) {
    pointerTypeRef.current = e.pointerType;
    if (e.pointerType !== "touch") return;
    const startX = e.clientX;
    const startY = e.clientY;
    function onPointerUp(upEvent: PointerEvent) {
      document.removeEventListener("pointercancel", onPointerCancel);
      pointerTypeRef.current = "";
      const dx = Math.abs(upEvent.clientX - startX);
      const dy = Math.abs(upEvent.clientY - startY);
      if (dx < 10 && dy < 10) {
        setOpen(true);
      }
    }
    function onPointerCancel() {
      document.removeEventListener("pointerup", onPointerUp);
      pointerTypeRef.current = "";
    }
    document.addEventListener("pointerup", onPointerUp, {
      once: true
    });
    document.addEventListener("pointercancel", onPointerCancel, {
      once: true
    });
  }
  React.useEffect(() => {
    if (!triggerRef.current) {
      return;
    }
    let triggerWidth = 0;
    options.forEach(option => {
      // create a reference with the same styles as the options to get the width of the text
      // the real option element can not be used because is not mounted until the dropdown is used
      const $reference = document.createElement("div");
      $reference.innerText = option.label;
      $reference.style.cssText = `
        font-family: Saans;
        font-size: ${isLg ? "16px" : "14px"};
        font-weight: 400;
        font-style: normal;
        letter-spacing: 0.16px;
        line-height: 1.1; 
        visibility: hidden;
        white-space: nowrap;
        position: absolute;
        z-index: -1;
      `;
      document.body.appendChild($reference);

      // Get the text width
      const textWidth = $reference.offsetWidth;
      // Add padding (16px on each side), gap (8px), and icon width (approximately 24px)
      const totalWidth = textWidth + 32 + 8 + 24;
      triggerWidth = Math.max(triggerWidth, totalWidth);
      document.body.removeChild($reference);
    });

    // Also check the placeholder width
    const $placeholderReference = document.createElement("div");
    $placeholderReference.innerText = placeholder;
    $placeholderReference.style.cssText = `
      font-family: Saans;
      font-size: ${isLg ? "16px" : "14px"};
      font-weight: 400;
      font-style: normal;
      letter-spacing: 0.16px;
      line-height: 1.1; 
      visibility: hidden;
      white-space: nowrap;
      position: absolute;
      z-index: -1;
    `;
    document.body.appendChild($placeholderReference);
    const placeholderWidth = $placeholderReference.offsetWidth + 32 + 8 + 24;
    triggerWidth = Math.max(triggerWidth, placeholderWidth);
    document.body.removeChild($placeholderReference);
    triggerRef.current.style.width = `${triggerWidth}px`;
  }, [options, isLg, placeholder]);
  return <RadixSelect.Root open={open} onOpenChange={handleOpenChange} onValueChange={next => onChange?.(fromInternal(next))} value={toInternal(value)} defaultValue={toInternal(defaultValue)} {...rest}>
      <Trigger ref={triggerRef} $hasValue={Boolean(value?.length)} aria-label={label} onPointerDown={handlePointerDown}>
        <RadixSelect.Value placeholder={placeholder} />
        <SelectIcon>
          <Icon icon={CaretDown} />
        </SelectIcon>
      </Trigger>

      <_StyledRadixSelectPortal>
        <Content position="popper"
      // TODO:
      // There is a bug in radix-ui that makes touch events to apply to elements behind the select, so this is just a workaround.
      // A fix is in the works, but it's not yet released:
      // https://github.com/radix-ui/primitives/issues/1658
      // https://github.com/radix-ui/primitives/pull/2085
      ref={ref => {
        ref?.addEventListener("touchstart", e => e.preventDefault());
        ref?.addEventListener("touchend", e => e.preventDefault());
      }}>
          <RadixSelect.ScrollUpButton>
            <Icon icon={CaretUp} />
          </RadixSelect.ScrollUpButton>
          <_StyledRadixSelectViewport>
            {internalOptions.map((option, idx) => <DropdownItem key={idx} {...option} />)}
          </_StyledRadixSelectViewport>
          <RadixSelect.ScrollDownButton>
            <Icon icon={CaretDown} />
          </RadixSelect.ScrollDownButton>
        </Content>
      </_StyledRadixSelectPortal>
    </RadixSelect.Root>;
}
var _StyledRadixSelectPortal = _styled2(RadixSelect.Portal).withConfig({
  componentId: "sc-sf-1l1zd4f-4"
})({
  "zIndex": "999999"
});
var _StyledRadixSelectViewport = /*#__PURE__*/_styled3(RadixSelect.Viewport).withConfig({
  displayName: "filter-dropdown___StyledRadixSelectViewport",
  componentId: "sc-1lc2w8l-0"
})({
  "userSelect": "none"
});